async function getJsonOrThrow(res) {
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`);
  }
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    const t = await res.text();
    throw new Error(`Expected JSON, got ${ct}. Snippet: ${t.slice(0, 200)}`);
  }
  return res.json();
}

 async function fetchSessionMedia(apiBase, type, sessionId, signal) {
  const res = await fetch(
     `${apiBase}session/media/${type}/${encodeURIComponent(sessionId)}`,
     { signal }
   );
   return getJsonOrThrow(res);
}

async function fetchAttemptMedia(apiBase, type, attemptId, signal) {
  const res = await fetch(
    `${apiBase}attempt/media/${type}/${encodeURIComponent(attemptId)}`,
    { signal }
  );
  return getJsonOrThrow(res);
}

// normalize contexts so "-pre" collapses under the final context
const normalizeContext = (ctx = "") => {
  const c = String(ctx).toLowerCase();
  if (c === "document-front-pre") return "document-front";
  if (c === "document-back-pre") return "document-back";
  if (c === "document-and-face-pre") return "document-and-face";
  if (c === "document-front-with-signature-pre") return "document-front-with-signature";
  if (c === "face-pre") return "face";
  return c;
};

// choose best item for a context: prefer final over "-pre", then larger size, then newer timestamp
const pickBetter = (a, b) => {
  const isFinal = (x) => !String(x?.context || "").toLowerCase().endsWith("-pre");
  const score = (x) => [
    isFinal(x) ? 1 : 0,                 // prefer final
    Number(x?.size || 0),               // bigger file
    Number(x?.timestamp || 0),          // newer
  ];
  const sa = score(a);
  const sb = score(b);
  // compare lexicographically
  for (let i = 0; i < sa.length; i++) {
    if (sa[i] > sb[i]) return a;
    if (sa[i] < sb[i]) return b;
  }
  return a; // tie: keep first
};

// reduce a list to one item per normalized context
const uniqueByContext = (list = []) => {
  const byCtx = new Map();
  for (const item of list) {
    const key = normalizeContext(item?.context);
    if (!key) continue;
    const current = byCtx.get(key);
    byCtx.set(key, current ? pickBetter(current, item) : item);
  }
  // keep a stable order (optional): common contexts first
  const order = ["document-front", "document-back", "selfie", "face", "portrait", "unknown"];
  const entries = Array.from(byCtx.entries());
  entries.sort((a, b) => {
    const ia = order.indexOf(a[0]); const ib = order.indexOf(b[0]);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
  return entries.map(([, v]) => v);
};

const isImage = (m) => {
  const mt = (m?.mimetype || m?.base64 || "").toString();
  return mt.startsWith("image") || mt.startsWith("data:image");
};
const isVideo = (m) => {
  const mt = (m?.mimetype || m?.base64 || "").toString();
  return mt.startsWith("video") || mt.startsWith("data:video");
};
const isSelfie = (m) => (m?.context || "").toLowerCase().includes("face");

function makeMediaSlides(list) {
  return list.map((m) => ({
    id: m.id,
    base64: m.base64,
    mimetype: m.mimetype || "", // used to decide img vs video
    label: normalizeContext(m.context),
    title: `${normalizeContext(m.context)}${
      m.mimetype ? ` • ${m.mimetype}` : ""
    }`,
    context: m.context,
  }));
}

export {
  fetchSessionMedia,
  fetchAttemptMedia,
  uniqueByContext,
  normalizeContext,
  makeMediaSlides,
  isImage,
  isVideo,
  isSelfie,
};
